/**
 * "VEIO NÚMERO?" — e a pergunta NÃO pode ser feita por truthiness.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════
 * ESTE ARQUIVO EXISTE PARA A DECISÃO SER UMA SÓ
 * ════════════════════════════════════════════════════════════════════════════════════════
 *
 * A regra nasceu em `pages/NotificacoesPage.js`, onde as três linhas do `switch` de títulos
 * perguntavam `numero_rdo ? …` e `numero_rnc || ''`. Nenhuma das duas pergunta "veio número?":
 *
 *   - `numero ? A : B` pergunta "veio número DIFERENTE DE ZERO?" — com `0` o rótulo cai no ramo
 *     do dado AUSENTE e imprime o genérico ("RDO Rejeitado"), que é justamente o card que o
 *     campo `numero_rdo` foi gravado para eliminar;
 *   - `numero || ''` tem a segunda metade: interpola o vazio NO MEIO da frase e imprime
 *     "RDO  · 12/08", com DOIS espaços — e `getByText('RDO · 12/08')` normaliza espaço em
 *     branco, então um teste escrito do jeito óbvio fica verde com o defeito de pé.
 *
 * A MESMA condição estava escrita em oito lugares do `src`. Duas cópias de uma decisão é como
 * este repo produz divergência (a pergunta "esta OS tem diário reprovado?" já esteve escrita em
 * três telas, com três respostas), então a régua mora aqui e cada tela importa.
 *
 * ALCANCE, dito por inteiro e sem inflar: `backend/models/all_campo.py:23` declara `numero_rdo`
 * como "sequencial POR OS (1, 2, 3...)", então HOJE o zero não nasce e a tela não chega nesse
 * estado pelo caminho normal. O que se conserta é a GUARDA, não um sintoma — condição que só
 * está certa por causa do intervalo dos dados quebra em silêncio no dia em que o intervalo muda,
 * e ninguém volta ao arquivo para conferir. É a classe "0 é falsy" que este repo já pagou no
 * campo numérico controlado que apagava a vírgula.
 *
 * O que É alcançável hoje, e não é hipótese: o RDO legado, gravado antes de `numero_rdo` existir,
 * chega com o campo ausente — e é ele que produz o espaço duplo em `RDO {n || ''} · <data>`.
 */

/** `''` fica de fora junto com `null`: string vazia é AUSÊNCIA de número, não o número zero. */
export const temNumero = (numero) => numero != null && numero !== '';

/**
 * `('RDO', 3, 'Rejeitado')` → `'RDO 3 Rejeitado'`; `('RDO', 0, …)` → `'RDO 0 Rejeitado'`;
 * `('RDO', null, 'Rejeitado')` → `'RDO Rejeitado'` (UM espaço, nunca dois).
 *
 * `sufixo` é opcional — `('RDO', 3)` → `'RDO 3'` e `('RDO', null)` → `'RDO'`, sem sobra no fim.
 * Isso importa porque o rótulo costuma ser costurado no meio de uma frase JSX
 * (`{rotuloComNumero('RDO', n)} · {data}`): um espaço pendurado no fim vira espaço duplo lá.
 */
export const rotuloComNumero = (prefixo, numero, sufixo) => (
  [prefixo, temNumero(numero) ? String(numero) : null, sufixo]
    .filter((parte) => parte != null && parte !== '')
    .join(' ')
);
